import { useEffect, useMemo, useState, useCallback } from 'react'
import {
    TITLE_BREAKDOWN_PROCESSING,
    PURCHASE_TYPE_AUTO_CHAIN,
    MAIN_TAB,
} from '@/constants'
import { useParams } from 'react-router-dom'
import * as actions from '@actions'
import * as store from '@store'
import { useWireState, useWireValue } from '@forminator/react-wire'
import { useRealtimeTitleBreakdownStatus } from '@/hook/useRealtimeTitleBreakdownStatus'
import { createSyntheticDownload } from '@/utils'
import { toast } from 'react-toastify'
import { useSearchParams } from 'react-router-dom'
import usePayments from '@/hook/usePayments'
import { useNavigate } from 'react-router-dom'
import { usePollTitleBreakdownStatus } from '@/hook/usePollTitleBreakdownStatus'
import { getRunsheetDocsByTab } from '../Runsheet/Runsheet.lib'

const TitleBreakdownViewModel = () => {
    
    const { id: runsheetId } = useParams()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    
    const [hasRecentFailure, setHasRecentFailure] = useState(false)
    const [exportLoading, setExportLoading] = useState(false)
    const [hasMissingData, setHasMissingData] = useState(false)
    
    const [titleBreakdown, setTitleBreakdown] = useWireState(store.titleBreakdown)
    const tabs = useWireValue(store.runsheetTabs)
    const runsheet = useWireValue(store.currentRunsheet)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const payments = usePayments()
    
    const activeTab = useMemo(() => searchParams.get('activeTab') || MAIN_TAB, [searchParams])
    const currentTab = useMemo(() => tabs?.find(t => t.id === activeTab) || tabs?.[0], [tabs, activeTab])
    
    const runsheetDocs = useMemo(() => getRunsheetDocsByTab(currentTab, runsheet), [runsheet, currentTab])
    
    const processTitleBreakdown = async (numDocuments, remainingBalance, fundingSource) => {
        
        setHasRecentFailure(false)
        
        await actions.processTitleBreakdown(runsheetId, numDocuments, activeTab, fundingSource)
        
        if (!remoteConfig?.realtimeEnabled)
            await actions.updateLocalCredits()
        
    }
    
    const handleTitleChain = async () => {
        
        if (!payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_AUTO_CHAIN,
            itemCount: runsheetDocs.length,
            onConfirmPayment:
                (remainingBalance, fundingSource) =>
                    processTitleBreakdown(runsheetDocs.length, remainingBalance, fundingSource),
        }))
            await processTitleBreakdown(runsheetDocs.length)
        
    }
    
    const exportTitleChain = async () => {
        
        setExportLoading(true)
        
        const res = await actions.exportTitleChain(runsheetId, activeTab)
        
        if (res)
            createSyntheticDownload(res.data, res.headers['content-type'], res.headers['content-name'])
        else
            toast.error('Failed to export Title Chain List, please try again.')
        
        setExportLoading(false)
        
    }
    
    const retryChaining = useCallback(
        () => actions.retryTitleChain(runsheetId, activeTab), 
        [runsheetId, activeTab])
    
    useRealtimeTitleBreakdownStatus(
        titleBreakdown?.id,
        runsheetId,
        activeTab,
        setHasRecentFailure)
    
    usePollTitleBreakdownStatus(
        TITLE_BREAKDOWN_PROCESSING.includes(titleBreakdown?.status) ? runsheetId : null,
        activeTab,
        setHasRecentFailure)
    
    const fetchChainData = async () => {
        
        const runsheet = await actions.fetchRunsheetById(runsheetId)
        const tabs = await actions.getTabsFromRunsheet(runsheetId)
        
        await actions.getChainsFromRunsheet(runsheetId)
        
        if (store.remoteConfig.getValue().chainer?.validateMissingData) {
            
            const tab = tabs.find(t => t.id === activeTab)
            const docs = getRunsheetDocsByTab(tab, runsheet)
            
            const missingData = docs.some(r =>
                !r.value.instrumentDate || !r.value.grantors.length || !r.value.grantees.length)
            
            setHasMissingData(missingData)
            
            if (missingData) return
            
        }
        
        const data = await actions.fetchTitleBreakdown(runsheetId, activeTab)
        
        if (data) {
            setTitleBreakdown(data)
            setHasRecentFailure(data.hasRecentFailure)
            
            await actions.listUniqueParties(runsheetId)
        } else {
            navigate('/runsheets')
        }
        
    }
    
    useEffect(() => {
        
        setTitleBreakdown(null)
        
        fetchChainData()
        
    }, [activeTab])
    
    return {
        
        // Params
        runsheetId,
        
        // Hooks
        navigate,
        searchParams,
        payments,
        
        // State
        hasRecentFailure,
        setHasRecentFailure,
        exportLoading,
        setExportLoading,
        hasMissingData,
        setHasMissingData,
        
        // Global State
        titleBreakdown,
        setTitleBreakdown,
        tabs,
        runsheet,
        remoteConfig,
        
        // Memos
        activeTab,
        currentTab,
        runsheetDocs,
        
        // Methods
        processTitleBreakdown,
        handleTitleChain,
        exportTitleChain,
        retryChaining,
        fetchChainData,
        
    }
    
}

export default TitleBreakdownViewModel
